import { GameEvent, GameState } from '../../../types/game';
import { toggleKept, allKept } from '../../../domain/dice';
import { makeDiceKeepEvent } from '../../../domain/events';
import { gameRules, makeEmptyDiceMask } from '../../../domain/gameRules';
import { getCurrentRollIndices, createIsCurrentRollDie } from '../../../domain/currentRollIndices';
import { computeStatusAfterKeep } from '../../../domain/turnCycleStateMachine';

export type ComputeToggleKeepResult =
  | { ok: false; reason: 'NOT_ONLINE' | 'NO_ACTIVE_PLAYER' | 'CAPTURED_DIE' | 'NON_SCORING' | 'NO_ROLL_INDEX' }
  | {
      ok: true;
      nextKept: boolean[];
      status: NonNullable<GameState['turnCycle']>['status'];
      event: GameEvent;
      keepRollIndex: number;
      enforceGroup: boolean;
      prevKept: boolean[];
      capturedMask: boolean[];
    };

export function computeToggleKeepResult(
  state: GameState,
  index: number,
  prevKeptOverride?: boolean[],
): ComputeToggleKeepResult {
  if (state.mode === 'REAL') return { ok: false, reason: 'NOT_ONLINE' };
  if (!state.turnCycle?.activePlayerId) return { ok: false, reason: 'NO_ACTIVE_PLAYER' };

  const capturedMask = state.turnCycle.capturedMask || makeEmptyDiceMask();
  const prevKept = (prevKeptOverride || state.turnCycle.kept || makeEmptyDiceMask())
    .map((flag, idx) => Boolean(flag) || capturedMask[idx]);
  if (capturedMask[index]) {
    return { ok: false, reason: 'CAPTURED_DIE' };
  }

  const diceValues = state.turnCycle.dice || [];
  const targetValue = diceValues[index];
  const togglingTo = !prevKept[index];
  const isCurrentRollDie = createIsCurrentRollDie(state.turnCycle);
  const currentRollIndices = getCurrentRollIndices(state.turnCycle, capturedMask);
  const currentRollDice = currentRollIndices.map((i) => diceValues[i]);

  if (togglingTo) {
    const sameValueCurrentRoll = currentRollIndices.filter((i) => diceValues[i] === targetValue).length;
    const uniq = Array.from(new Set(currentRollDice)).sort((a, b) => a - b);
    const fullStraight = currentRollDice.length === gameRules.dice.count && uniq.length === gameRules.dice.count;
    const isStraightRoll = fullStraight && (
      (uniq[0] === 1 && uniq[gameRules.dice.count - 1] === gameRules.dice.count)
      || (uniq[0] === 2 && uniq[gameRules.dice.count - 1] === gameRules.dice.faces)
    );
    const canScore = targetValue === 1 || targetValue === 5 || sameValueCurrentRoll >= 3 || isStraightRoll;
    if (!canScore) {
      return { ok: false, reason: 'NON_SCORING' };
    }
  }

  const groupedIndices = diceValues
    .map((_, i) => i)
    .filter((i) => diceValues[i] === targetValue && !capturedMask[i] && isCurrentRollDie(i));
  const enforceGroup = groupedIndices.length >= 3;

  const nextKept = (() => {
    if (!enforceGroup) return toggleKept(prevKept, index);
    const targetState = !prevKept[index];
    const groupSet = new Set(groupedIndices);
    return prevKept.map((k, i) => (groupSet.has(i) ? targetState : k));
  })().map((flag, idx) => flag || capturedMask[idx]);

  const keepRollIndex = typeof state.turnCycle.lastRoll?.rollIndex === 'number'
    ? state.turnCycle.lastRoll.rollIndex
    : (state.turnCycle.rollCount > 0 ? state.turnCycle.rollCount - 1 : -1);
  if (keepRollIndex < 0) {
    return { ok: false, reason: 'NO_ROLL_INDEX' };
  }

  return {
    ok: true,
    nextKept,
    status: computeStatusAfterKeep(state.turnCycle.status, allKept(nextKept)),
    event: makeDiceKeepEvent(state.turnNumber, state.turnCycle.activePlayerId, keepRollIndex, nextKept),
    keepRollIndex,
    enforceGroup,
    prevKept,
    capturedMask,
  };
}

export function handleApplyDiceKeep(state: GameState, playerId: string, rollIndex: number, keptMask: boolean[]): GameState {
  if (state.mode === 'REAL' || !state.turnCycle) return state;
  if (state.turnCycle.activePlayerId !== playerId) return state;
  const currentRollIndex = typeof state.turnCycle.lastRoll?.rollIndex === 'number'
    ? state.turnCycle.lastRoll.rollIndex
    : (state.turnCycle.rollCount > 0 ? state.turnCycle.rollCount - 1 : -1);
  if (rollIndex !== currentRollIndex) return state;
  const capturedMask = state.turnCycle.capturedMask || makeEmptyDiceMask();
  const normalizedKeptMask = keptMask.map((flag, index) => flag || capturedMask[index]);
  const status = computeStatusAfterKeep(state.turnCycle.status, allKept(normalizedKeptMask));
  return {
    ...state,
    turnCycle: {
      ...state.turnCycle,
      kept: normalizedKeptMask,
      status,
    },
  };
}
